import type { Sistema } from '@/models'
import { formatCurrency, formatDate } from '@/utils/formatter'
import { FaServer, FaBuilding, FaUser, FaBox, FaUserShield, FaCalendarAlt, FaMoneyBillWave, FaNetworkWired, FaLifeRing, FaLink } from 'react-icons/fa'

interface SistemaViewCardProps {
  sistema: Sistema
}

export const SistemaViewCard = ({ sistema }: SistemaViewCardProps) => {
  return (
    <div className="space-y-6 p-6">
      {/* Cabeçalho */}
      <div className="border-b border-gray-200 dark:border-neutral-700 pb-4">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <FaServer className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {sistema.nome}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {sistema.descricao}
            </p>
            <div className="flex gap-2 mt-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                sistema.categoria === 'Software de Negócio' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                sistema.categoria === 'Banco de Dados' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                sistema.categoria === 'Sistema Operacional' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
              }`}>
                {sistema.categoria}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                Versão {sistema.versao_atual}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                {sistema.status_ciclo_vida}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Informações de Responsabilidade */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <FaBuilding className="text-blue-600 dark:text-blue-400" />
            Setor Proprietário
          </h3>
          <p className="text-gray-900 dark:text-gray-100 pl-6">
            {sistema.proprietario?.nome_setor || '-'}
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <FaUser className="text-blue-600 dark:text-blue-400" />
            Gestor Técnico
          </h3>
          <p className="text-gray-900 dark:text-gray-100 pl-6">
            {sistema.gestor_tecnico?.name || '-'}
            {sistema.gestor_tecnico?.email && (
              <span className="block text-sm text-gray-500 dark:text-gray-400">
                {sistema.gestor_tecnico.email}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Licenciamento */}
      <div className="bg-gray-50 dark:bg-neutral-900 rounded-lg p-4 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FaMoneyBillWave className="text-green-600 dark:text-green-400" />
          Licenciamento e Custos
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Fornecedor</p>
            <p className="text-gray-900 dark:text-gray-100 font-medium">{sistema.fornecedor}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Tipo de Licença</p>
            <p className="text-gray-900 dark:text-gray-100 font-medium">{sistema.tipo_licenca}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Qtd. Licenças</p>
            <p className="text-gray-900 dark:text-gray-100 font-medium">{sistema.quantidade_licencas}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Custo Anual</p>
            <p className="font-medium text-green-600 dark:text-green-400">
              {formatCurrency(sistema.custo_anual)}
            </p>
          </div>
          {sistema.data_expiracao && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                <FaCalendarAlt className="h-3 w-3" /> Data de Expiração
              </p>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{formatDate(sistema.data_expiracao)}</p>
            </div>
          )}
          {sistema.data_renovacao && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                <FaCalendarAlt className="h-3 w-3" /> Data de Renovação
              </p>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{formatDate(sistema.data_renovacao)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Infraestrutura */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FaNetworkWired className="text-purple-600 dark:text-purple-400" />
          Infraestrutura e Serviços
        </h3>
        
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Servidores/Infraestrutura</p>
            <p className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-neutral-900 p-3 rounded-md whitespace-pre-wrap">
              {sistema.servidores_infraestrutura}
            </p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Serviço de Negócio Relacionado</p>
            <p className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-neutral-900 p-3 rounded-md whitespace-pre-wrap">
              {sistema.servico_negocio_relacionado}
            </p>
          </div>

          {sistema.documentacao_link && (
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                <FaLink className="h-3 w-3" /> Documentação
              </p>
              <a 
                href={sistema.documentacao_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline block truncate"
              >
                {sistema.documentacao_link}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Suporte */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FaLifeRing className="text-red-600 dark:text-red-400" />
          Informações de Suporte
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Suporte DR</p>
            <p className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-neutral-900 p-3 rounded-md text-sm whitespace-pre-wrap">
              {sistema.suporte_dr}
            </p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Suporte DN</p>
            <p className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-neutral-900 p-3 rounded-md text-sm whitespace-pre-wrap">
              {sistema.suporte_dn}
            </p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Suporte Fornecedor</p>
            <p className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-neutral-900 p-3 rounded-md text-sm whitespace-pre-wrap">
              {sistema.suporte_fornecedor}
            </p>
          </div>
        </div>
      </div>

      {/* Módulos e Perfis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Módulos */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FaBox className="text-blue-600 dark:text-blue-400" />
            Módulos ({sistema.modulos?.length || 0})
          </h3>
          {sistema.modulos && sistema.modulos.length > 0 ? (
            <div className="space-y-2">
              {sistema.modulos.map((modulo) => (
                <div 
                  key={modulo.id} 
                  className="bg-gray-50 dark:bg-neutral-900 p-3 rounded-md"
                >
                  <p className="font-medium text-gray-900 dark:text-gray-100">{modulo.nome}</p>
                  {modulo.descricao && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{modulo.descricao}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm italic">
              Nenhum módulo cadastrado
            </p>
          )}
        </div>

        {/* Perfis */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FaUserShield className="text-purple-600 dark:text-purple-400" />
            Perfis de Acesso ({sistema.perfis?.length || 0})
          </h3>
          {sistema.perfis && sistema.perfis.length > 0 ? (
            <div className="space-y-2">
              {sistema.perfis.map((perfil) => (
                <div 
                  key={perfil.id} 
                  className="bg-gray-50 dark:bg-neutral-900 p-3 rounded-md"
                >
                  <p className="font-medium text-gray-900 dark:text-gray-100">{perfil.nome}</p>
                  {perfil.descricao && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{perfil.descricao}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm italic">
              Nenhum perfil cadastrado
            </p>
          )}
        </div>
      </div>

      {/* Rodapé com datas */}
      {(sistema.created_at || sistema.updated_at) && (
        <div className="border-t border-gray-200 dark:border-neutral-700 pt-4 mt-6">
          <div className="flex gap-6 text-xs text-gray-500 dark:text-gray-400">
            {sistema.created_at && (
              <div>
                <span className="font-medium">Criado em:</span> {formatDate(sistema.created_at)}
              </div>
            )}
            {sistema.updated_at && (
              <div>
                <span className="font-medium">Atualizado em:</span> {formatDate(sistema.updated_at)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
